import React, { useState, useEffect, useRef, useCallback } from 'react';
import SpecialistDashboardLayout from '../../components/SpecialistDashboardLayout';
import { useSpecialistAuth } from '../../context/SpecialistAuthContext';
import { API_BASE_URL } from '../../config/api';
import '../MySupportPage/MySupportPage.css';

const SpecialistSupportPage = () => {
  const { specialist } = useSpecialistAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTicket, setOpenTicket] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const chatEndRef = useRef(null);

  // New ticket form
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '', category: 'General' });
  const [creating, setCreating] = useState(false);

  const specEmail = specialist?.email || '';
  const specName = specialist?.name || 'Specialist';

  const fetchTickets = useCallback(async (silent = false) => {
    if (!specEmail) return;
    try {
      if (!silent) setLoading(true);
      const res = await fetch(`${API_BASE_URL}/support/user-tickets/${encodeURIComponent(specEmail)}`);
      const data = await res.json();
      if (data.success) setTickets(data.tickets);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  }, [specEmail]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Live polling - refresh tickets every 5s
  useEffect(() => {
    if (!specEmail) return;
    const interval = setInterval(() => fetchTickets(true), 5000);
    return () => clearInterval(interval);
  }, [specEmail, fetchTickets]);

  // Live polling - refresh open conversation every 4s
  useEffect(() => {
    if (!openTicket) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/support/tickets/${openTicket.id}`);
        const data = await res.json();
        if (data.success) setOpenTicket(data.ticket);
      } catch (e) { /* ignore */ }
    }, 4000);
    return () => clearInterval(interval);
  }, [openTicket?.id]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [openTicket]);

  const handleOpenChat = async (ticketId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/support/tickets/${ticketId}`);
      const data = await res.json();
      if (data.success) setOpenTicket(data.ticket);
    } catch (error) {
      console.error('Error fetching ticket:', error);
    }
  };

  const handleSendChat = async () => {
    if (!chatMessage.trim() || !openTicket) return;
    setSendingChat(true);
    try {
      const res = await fetch(`${API_BASE_URL}/support/tickets/${openTicket.id}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          senderId: null,
          senderName: specName,
          senderEmail: specEmail,
          senderRole: 'user',
          message: chatMessage.trim()
        })
      });
      const data = await res.json();
      if (data.success) {
        setChatMessage('');
        handleOpenChat(openTicket.id);
        fetchTickets(true);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingChat(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.message) {
      alert('Please fill in subject and message');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/support/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: null,
          userName: specName,
          userEmail: specEmail,
          subject: newTicket.subject,
          message: newTicket.message,
          category: newTicket.category || 'General',
          priority: 'Medium'
        })
      });
      const data = await res.json();
      if (data.success) {
        setShowNewTicket(false);
        setNewTicket({ subject: '', message: '', category: 'General' });
        fetchTickets(true);
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-CA');
  };

  const formatChatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Open': return 'ms-status-open';
      case 'In Progress': return 'ms-status-progress';
      case 'Resolved': return 'ms-status-resolved';
      case 'Closed': return 'ms-status-closed';
      default: return '';
    }
  };

  if (!specialist) {
    return (
      <SpecialistDashboardLayout>
        <div className="ms-container">
          <div className="ms-login-prompt">
            <h2>Please log in to view your support tickets</h2>
          </div>
        </div>
      </SpecialistDashboardLayout>
    );
  }

  // ====== CONVERSATION VIEW ======
  if (openTicket) {
    const replies = openTicket.replies || [];
    return (
      <SpecialistDashboardLayout>
        <div className="ms-container">
          <div className="ms-chat-wrapper">
            <div className="ms-chat-header">
              <button className="ms-back-btn" onClick={() => { setOpenTicket(null); setChatMessage(''); }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Back
              </button>
              <div className="ms-chat-title-area">
                <span className="ms-chat-ticket-no">{openTicket.ticketNo}</span>
                <span className={`ms-status-badge ${getStatusClass(openTicket.status)}`}>{openTicket.status}</span>
              </div>
            </div>

            <div className="ms-chat-subject">
              <h3>{openTicket.subject}</h3>
              <p>Category: {openTicket.category} &bull; Created: {formatDate(openTicket.createdAt)}</p>
            </div>

            <div className="ms-chat-messages">
              {/* Original message */}
              <div className="ms-bubble ms-bubble-user">
                <div className="ms-bubble-avatar ms-avatar-user">
                  {openTicket.userName?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="ms-bubble-body">
                  <div className="ms-bubble-meta">
                    <span className="ms-bubble-name">{openTicket.userName} (You)</span>
                    <span className="ms-bubble-time">{formatChatTime(openTicket.createdAt)}</span>
                  </div>
                  <div className="ms-bubble-text ms-text-user">{openTicket.message}</div>
                </div>
              </div>

              {/* Legacy admin reply */}
              {replies.length === 0 && openTicket.adminReply && (
                <div className="ms-bubble ms-bubble-admin">
                  <div className="ms-bubble-avatar ms-avatar-admin">A</div>
                  <div className="ms-bubble-body">
                    <div className="ms-bubble-meta">
                      <span className="ms-bubble-name">Support Team <span className="ms-admin-badge">Admin</span></span>
                      <span className="ms-bubble-time">{formatChatTime(openTicket.repliedAt)}</span>
                    </div>
                    <div className="ms-bubble-text ms-text-admin">{openTicket.adminReply}</div>
                  </div>
                </div>
              )}

              {/* Threaded replies */}
              {replies.map((reply) => (
                <div key={reply.id} className={`ms-bubble ${reply.senderRole === 'admin' ? 'ms-bubble-admin' : 'ms-bubble-user'}`}>
                  <div className={`ms-bubble-avatar ${reply.senderRole === 'admin' ? 'ms-avatar-admin' : 'ms-avatar-user'}`}>
                    {reply.senderRole === 'admin' ? 'A' : reply.senderName?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="ms-bubble-body">
                    <div className="ms-bubble-meta">
                      <span className="ms-bubble-name">
                        {reply.senderRole === 'admin' ? 'Support Team' : reply.senderName}
                        {reply.senderRole === 'admin' && <span className="ms-admin-badge">Admin</span>}
                        {reply.senderRole === 'user' && ' (You)'}
                      </span>
                      <span className="ms-bubble-time">{formatChatTime(reply.createdAt)}</span>
                    </div>
                    <div className={`ms-bubble-text ${reply.senderRole === 'admin' ? 'ms-text-admin' : 'ms-text-user'}`}>
                      {reply.message}
                    </div>
                  </div>
                </div>
              ))}

              <div ref={chatEndRef} />
            </div>

            {/* Input */}
            {openTicket.status !== 'Closed' && (
              <div className="ms-chat-input-area">
                <textarea
                  className="ms-chat-input"
                  placeholder="Type your message..."
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  rows="2"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); } }}
                />
                <button className="ms-send-btn" onClick={handleSendChat} disabled={sendingChat || !chatMessage.trim()}>
                  {sendingChat ? '...' : (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M18.3332 1.66669L9.1665 10.8334M18.3332 1.66669L12.4998 18.3334L9.1665 10.8334M18.3332 1.66669L1.6665 7.50002L9.1665 10.8334" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  )}
                </button>
              </div>
            )}
            {openTicket.status === 'Closed' && (
              <div className="ms-chat-closed-notice">This ticket has been closed.</div>
            )}
          </div>
        </div>
      </SpecialistDashboardLayout>
    );
  }

  // ====== TICKET LIST VIEW ======
  return (
    <SpecialistDashboardLayout>
      <div className="ms-container">
        <div className="ms-header">
          <div>
            <h1 className="ms-title">Support</h1>
            <p className="ms-subtitle">View and manage your support conversations</p>
          </div>
          <button className="ms-new-ticket-btn" onClick={() => setShowNewTicket(true)}>
            + New Ticket
          </button>
        </div>

        {loading ? (
          <div className="ms-loading">Loading your tickets...</div>
        ) : tickets.length === 0 ? (
          <div className="ms-empty">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <path d="M32 56C45.2548 56 56 45.2548 56 32C56 18.7452 45.2548 8 32 8C18.7452 8 8 18.7452 8 32C8 45.2548 18.7452 56 32 56Z" stroke="#CBD5E1" strokeWidth="2"/>
              <path d="M24 28H40M24 36H36" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <h3>No support tickets yet</h3>
            <p>Create a ticket to start a conversation with our support team.</p>
            <button className="ms-new-ticket-btn" onClick={() => setShowNewTicket(true)}>Create a Ticket</button>
          </div>
        ) : (
          <div className="ms-ticket-list">
            {tickets.map((ticket) => (
              <div key={ticket.id} className="ms-ticket-card" onClick={() => handleOpenChat(ticket.id)}>
                <div className="ms-ticket-top">
                  <span className="ms-ticket-no">{ticket.ticketNo}</span>
                  <span className={`ms-status-badge ${getStatusClass(ticket.status)}`}>{ticket.status}</span>
                </div>
                <h3 className="ms-ticket-subject">{ticket.subject}</h3>
                <p className="ms-ticket-preview">{ticket.message?.substring(0, 100)}{ticket.message?.length > 100 ? '...' : ''}</p>
                <div className="ms-ticket-bottom">
                  <span className="ms-ticket-cat">{ticket.category}</span>
                  <span className="ms-ticket-date">{formatDate(ticket.createdAt)}</span>
                  {ticket.replies?.length > 0 && (
                    <span className="ms-ticket-replies">{ticket.replies.length} {ticket.replies.length === 1 ? 'reply' : 'replies'}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Ticket Modal */}
      {showNewTicket && (
        <div className="ms-modal-overlay" onClick={() => setShowNewTicket(false)}>
          <div className="ms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ms-modal-header">
              <h2>New Support Ticket</h2>
              <button className="ms-modal-close" onClick={() => setShowNewTicket(false)}>&times;</button>
            </div>
            <div className="ms-modal-body">
              <div className="ms-form-group">
                <label>Category</label>
                <select value={newTicket.category} onChange={(e) => setNewTicket({...newTicket, category: e.target.value})} className="ms-select">
                  <option value="General">General</option>
                  <option value="Billing">Billing</option>
                  <option value="Account">Account</option>
                  <option value="Technical">Technical</option>
                  <option value="Verification">Verification</option>
                </select>
              </div>
              <div className="ms-form-group">
                <label>Subject *</label>
                <input type="text" value={newTicket.subject} onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})} className="ms-input" placeholder="Brief description of your issue" />
              </div>
              <div className="ms-form-group">
                <label>Message *</label>
                <textarea value={newTicket.message} onChange={(e) => setNewTicket({...newTicket, message: e.target.value})} className="ms-textarea" rows="5" placeholder="Describe your issue in detail..." />
              </div>
              <div className="ms-modal-actions">
                <button className="ms-btn ms-btn-cancel" onClick={() => setShowNewTicket(false)}>Cancel</button>
                <button className="ms-btn ms-btn-submit" onClick={handleCreateTicket} disabled={creating}>
                  {creating ? 'Submitting...' : 'Submit Ticket'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </SpecialistDashboardLayout>
  );
};

export default SpecialistSupportPage;
